/**
 * Serializes form data into the desired API format
 */
export const serializeForm = (formElement, formConfig) => {
    // Create base data structure
    const result = {
        content_json: {},
        policy_at: null // Initialize policy_at
    };

    // Add form ID to form data if available+
    if (formConfig.id) { result["form_id"] =  formConfig.id; }

    // Check policy checkbox for both form types
    const policyCheckboxSimple = formElement.querySelector('#privacyCheckboxSimple');
    const policyCheckboxMulti = formElement.querySelector('#privacyCheckboxMulti');
    
    if ((policyCheckboxSimple && policyCheckboxSimple.checked) || 
        (policyCheckboxMulti && policyCheckboxMulti.checked)) {
        // Set current date in ISO format when policy is accepted
        result.policy_at = new Date().toISOString();
    }

    // Group form entries by Steps/Sections
    const groupedData = {};
    
    // Initialize structure based on formConfig 
    if (formConfig.steps) {
        formConfig.steps.forEach(step => {
            if (step.active === 1) {
                groupedData[step.title] = {};
                if (step.items) {
                    step.items.forEach((item, index) => {
                        const label = item.label || `item-${index}`;
                        // Use the label as the key if available, otherwise fallback to index
                        // Treat all options as checkbox (including radio buttons)
                        if (item.type === 9 || item.type === 10) {
                            groupedData[step.title][label] = "0";
                        } else {
                            groupedData[step.title][label] = null;
                        }
                    });
                }
            }
        });
    }

    // Collect all form elements, including hidden ones!
    const allInputs = formElement.querySelectorAll('input, select, textarea');
    allInputs.forEach(input => {
        // Skip privacy checkbox
        if (input.id === 'privacyCheckboxSimple' || input.id === 'privacyCheckboxMulti') return;

        const name = input.name;
        if (!name) return;

        // IMPORTANT: Also process hidden inputs
        let value = input.value;
        
        // Check if the field is explicitly marked as top-level or is in the contact section
        const isTopLevel = input.hasAttribute('data-serialize-in-top-level');
        const isInContactSection = input.closest('.contact-information') !== null;
        
        // Serialize top-level fields directly in the main object (Event ID or contact fields)
        if (isTopLevel || isInContactSection) {
            // If a custom top level key is provided, use it instead of the original name
            const topLevelKey = input.getAttribute('data-top-level-key') || name;
            
            // Special handling for is_anonymous to convert to boolean
            if (name === 'is_anonymous') {
                result[topLevelKey] = value === 'anonymous';
            } else {
                result[topLevelKey] = value;
            }
            
            return; // Early return for top-level fields
        }

        // Special case for "Additional Notes" field
        if (name === "Weitere Hinweise (z.B. bezüglich Nahrungsmittelallergien, Barrierefreiheit)") {
            if (!groupedData["Weitere Hinweise"]) {
                groupedData["Weitere Hinweise"] = {};
            }
            groupedData["Weitere Hinweise"]["Weitere Hinweise"] = value || null;
            return;
        }

        // Try to use data-serial-key attribute if available
        const serialKey = input.getAttribute('data-serial-key');
        if (serialKey) {
            // Parse the new format: encodedStepTitle-encodedLabel
            const keyParts = serialKey.split('/');
            if (keyParts.length === 4) {
                let sectionName, fieldName;
                try {
                    sectionName = decodeURIComponent(keyParts[0]);
                    fieldName = decodeURIComponent(keyParts[1]);
                    
                    if (sectionName && fieldName) {
                        if (!groupedData[sectionName]) {
                            groupedData[sectionName] = {};
                        }

                        if (input.type === 'checkbox' || input.type === 'radio') {
                            if (input.checked) {
                                if (input.type === 'radio') {
                                    const allRadios = formElement.querySelectorAll(`input[name="${input.name}"]`);
                                    allRadios.forEach(radio => {
                                        const radioSerialKey = radio.getAttribute('data-serial-key');
                                        if (radioSerialKey) {
                                            const radioParts = radioSerialKey.split('/');
                                            if (radioParts.length === 4) {
                                                try {
                                                    const radioSection = decodeURIComponent(radioParts[0]);
                                                    const radioField = decodeURIComponent(radioParts[1]);
                                                    
                                                    if (radioSection === sectionName) {
                                                        groupedData[sectionName][radioField] = radio === input ? "1" : "0";
                                                    }
                                                } catch (e) {
                                                    console.error("Error decoding radio serial key parts:", e);
                                                }
                                            }
                                        } else {
                                            const radioFieldName = radio.value || radio.dataset.label || "";
                                            if (radioFieldName) {
                                                groupedData[sectionName][radioFieldName] = radio === input ? "1" : "0";
                                            }
                                        }
                                    });
                                } else {
                                    groupedData[sectionName][fieldName] = "1";
                                }
                            } else if (input.type === 'checkbox') {
                                groupedData[sectionName][fieldName] = "0";
                            }
                        } else {
                            groupedData[sectionName][fieldName] = value || null;
                        }
                        
                        return; // We've processed this input with its data-serial-key
                    }
                } catch (e) {
                    console.error("Error decoding serial key parts:", e);
                }
            }
        }

        // Fallback: All other fields go into content_json using the old dot notation
        const dotParts = name.split('.');
        if (dotParts.length >= 2) {
            const sectionName = dotParts[0];
            const fieldName = dotParts[1];
            
            if (sectionName && fieldName) {
                if (!groupedData[sectionName]) {
                    groupedData[sectionName] = {};
                }

                if (input.type === 'checkbox' || input.type === 'radio') {
                    if (input.checked) {
                        if (input.type === 'radio') {
                            const allRadios = formElement.querySelectorAll(`input[name="${input.name}"]`);
                            allRadios.forEach(radio => {
                                const radioFieldName = radio.value || radio.dataset.label || radio.id.split('.')[1];
                                groupedData[sectionName][radioFieldName] = radio === input ? "1" : "0";
                            });
                        } else {
                            groupedData[sectionName][fieldName] = "1";
                        }
                    } else if (input.type === 'checkbox') {
                        groupedData[sectionName][fieldName] = "0";
                    }
                } else {
                    groupedData[sectionName][fieldName] = value || null;
                }
            }
        }
    });

    // Add grouped data to content_json
    result.content_json = groupedData;


    return result;
};
